import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import ToolShell from './ToolShell';
import { Loader2, ArrowRight, Copy, Check, Download, ExternalLink, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const PENDING_IMPORT_KEY = 'ff_pending_import';

/**
 * Generic platform → Fantastic Forms importer. Each individual tool
 * page (Google Forms, Tally, Typeform, JotForm) renders this shell
 * with a different `platform` prop + on-page copy. The backend
 * dispatches to the right parser.
 *
 * Auth-aware: signed-in users get a "Open in builder" CTA (form is
 * auto-created as Draft). Anonymous users get a "Sign up to open in
 * builder" CTA; the parsed schema is stashed in localStorage and
 * auto-claimed after login (see Login.js).
 */
export default function FormImporter({
  platform,
  slug,
  platformName,
  metaTitle,
  tagline,
  urlExample,
  helpText,
}) {
  const [url, setUrl] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const json = useMemo(() => (result ? JSON.stringify(result, null, 2) : ''), [result]);

  const run = async () => {
    if (!url.trim()) { toast.error(`Paste a ${platformName} URL first`); return; }
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const { data } = await axios.post(
        `${API}/api/tools/import-form`,
        { platform, url: url.trim() },
        { timeout: 30000, headers }
      );
      setResult(data);
      toast.success(`Imported "${data.title}" (${(data.fields || []).length} fields)`);

      if (!data.form_id) {
        // Anonymous: stash parsed schema so we can claim it after sign-up.
        try {
          localStorage.setItem(PENDING_IMPORT_KEY, JSON.stringify({
            platform: data.platform,
            source_url: data.source_url,
            title: data.title,
            fields: data.fields || [],
            stashed_at: Date.now(),
          }));
        } catch { /* localStorage may be unavailable; non-fatal */ }
      } else {
        // Authed: form already created — clear any stale pending import.
        try { localStorage.removeItem(PENDING_IMPORT_KEY); } catch { /* ignore */ }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not import that form.');
    } finally {
      setRunning(false);
    }
  };

  const copyJson = async () => {
    if (!json) return;
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      toast.success('JSON copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Could not access clipboard'); }
  };

  const download = () => {
    if (!json) return;
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = `${platform}-${(result?.title || 'form').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(u);
  };

  const openInBuilder = () => {
    if (result?.form_id) {
      navigate(`/builder/${result.form_id}`);
    }
  };

  const signupAndImport = () => {
    // localStorage already holds the parsed schema (set in run()).
    // After signup → email verify → login, Login.js claims it and routes to /builder/:id.
    navigate('/signup');
  };

  return (
    <ToolShell slug={slug} title={metaTitle} h1={`Import from ${platformName}`} tagline={tagline}>
      <div className="rounded-xl border border-zinc-200 bg-white p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="imp-url" className="text-sm">{platformName} form URL</Label>
          <Input
            id="imp-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={urlExample}
            className="font-mono text-sm"
            data-testid="imp-url"
          />
          <p className="text-[11px] text-zinc-500">{helpText}</p>
        </div>
        <Button
          onClick={run}
          disabled={running || !url.trim()}
          className="bg-[#FE6A6B] hover:bg-[#e85f60] text-white"
          data-testid="imp-run"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ArrowRight className="w-4 h-4 mr-1.5" />}
          {running ? 'Importing…' : `Import from ${platformName}`}
        </Button>
        {error && <p className="text-sm text-red-600" data-testid="imp-error">{error}</p>}
      </div>

      {result && (
        <div className="rounded-xl border border-zinc-200 bg-white p-6 space-y-4" data-testid="imp-result">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs text-zinc-500">Imported form</p>
              <h3 className="text-lg font-semibold text-zinc-900">{result.title}</h3>
              <p className="text-xs text-zinc-500 mt-0.5">{(result.fields || []).length} field{(result.fields || []).length === 1 ? '' : 's'} detected</p>
            </div>
            {result.form_id ? (
              <Button
                onClick={openInBuilder}
                className="bg-[#FE6A6B] hover:bg-[#e85f60] text-white"
                data-testid="imp-open-builder"
              >
                <Wand2 className="w-4 h-4 mr-1.5" />
                Open in builder
                <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-80" />
              </Button>
            ) : (
              <Button
                onClick={signupAndImport}
                className="bg-[#FE6A6B] hover:bg-[#e85f60] text-white"
                data-testid="imp-signup-and-open"
              >
                <Wand2 className="w-4 h-4 mr-1.5" />
                Sign up to open in builder
              </Button>
            )}
          </div>

          <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100">
            {(result.fields || []).map((f, i) => (
              <div key={i} className="px-3 py-2 flex items-start gap-3 text-sm">
                <span className="shrink-0 text-zinc-400 tabular-nums w-6 text-right">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-900 break-words">{f.label}{f.required ? <span className="text-red-500 ml-1">*</span> : null}</p>
                  {Array.isArray(f.options) && f.options.length > 0 && (
                    <p className="text-[11px] text-zinc-500 mt-0.5">Options: {f.options.slice(0, 6).join(', ')}{f.options.length > 6 ? `, +${f.options.length - 6} more` : ''}</p>
                  )}
                </div>
                <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-100 text-zinc-700 font-mono">{f.type}</span>
              </div>
            ))}
          </div>

          {!isAuthenticated && (
            <div className="rounded-md bg-[#FFF1F1] border border-[#FE6A6B]/30 p-3 text-xs text-zinc-700">
              <p className="font-medium text-zinc-900 mb-0.5">Already imported. Sign up to claim it</p>
              <p>Create a free Fantastic Forms account and we'll drop you straight into the editor with all {(result.fields || []).length} fields pre-loaded as a Draft.</p>
            </div>
          )}

          <details className="text-sm">
            <summary className="cursor-pointer text-zinc-600 hover:text-zinc-900">Or grab the raw JSON</summary>
            <div className="mt-3 space-y-1.5">
              <Label className="text-sm">Normalized JSON</Label>
              <Textarea readOnly value={json} rows={10} className="font-mono text-xs" data-testid="imp-json" />
              <div className="flex gap-2">
                <Button onClick={copyJson} variant="outline" data-testid="imp-copy">
                  {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                  {copied ? 'Copied' : 'Copy JSON'}
                </Button>
                <Button variant="outline" onClick={download} data-testid="imp-download">
                  <Download className="w-4 h-4 mr-1.5" />
                  Download .json
                </Button>
              </div>
            </div>
          </details>
        </div>
      )}

      <section className="prose prose-sm max-w-none text-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 mt-2">How it works</h2>
        <p>
          We fetch the publicly-rendered HTML of your {platformName} form server-side, parse the
          embedded form definition, and recreate it as a Draft inside Fantastic Forms. Every
          field's label, type, required flag, and options mapped 1-to-1. Your form data never
          leaves our server beyond the single read needed to parse it.
        </p>
        <h3 className="text-base font-semibold text-zinc-900 mt-4">What gets imported</h3>
        <ul>
          <li>Form title</li>
          <li>Every supported field's label, type, and required flag</li>
          <li>Multiple-choice / dropdown / checkbox options</li>
        </ul>
        <p className="text-zinc-500">
          What doesn't get imported: branding, conditional logic, advanced validations, and third-party integrations.
          Payment fields are imported as a Fantastic Forms <strong>Payment</strong> element — open it after import
          to paste your provider API keys (Stripe, PayPal, Paddle, Polar, Creem, Dodo or Lemon Squeezy) and confirm the price.
        </p>
      </section>
    </ToolShell>
  );
}
